import { useState, useEffect } from "react";
import { Bell, Plus, Menu, User, Settings, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/ui/logo";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { RouteDisplay } from "../navigation/RouteDisplay";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface HeaderProps {
  profile?: any;
  user?: any;
  logout: () => void;
  isMobile: boolean;
  isSidebarExpanded: boolean;
  toggleSidebar: () => void;
  onAddNewTransaction: () => void;
}

export function Header({
  profile,
  user,
  logout,
  isMobile,
  isSidebarExpanded,
  toggleSidebar,
  onAddNewTransaction,
}: HeaderProps) {
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");

  useEffect(() => {
    async function fetchCompanyLogo() {
      if (!profile?.company_id) {
        return;
      }

      try {
        const { data: companyData, error } = await supabase
          .from("companies")
          .select("logo_url, name")
          .eq("id", profile.company_id)
          .maybeSingle();

        if (error) {
          console.error("Error fetching company logo:", error);
          return;
        }

        if (companyData) {
          setCompanyLogoUrl(companyData.logo_url);
          setCompanyName(companyData.name || "");
        }
      } catch (error) {
        console.error("Unexpected error fetching company logo:", error);
      }
    }

    fetchCompanyLogo();
  }, [profile?.company_id]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logout realizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao fazer logout');
    }
  };

  const handleLogoClick = () => {
    window.location.href = '/';
  };

  return (
    <header
      className={cn(
        "sticky top-0 left-0 right-0 z-60 h-16 bg-background border-b-0 transition-all duration-300"
      )}
    >
      <div className="flex items-center h-full px-4">
        {/* Left Section: Logo + Company Name + Route Display */}
        <div className="flex items-center gap-3 justify-self-start">
          {/* Mobile Menu Toggle */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="text-foreground/80 hover:text-foreground hover:bg-muted transition-all duration-200"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Logo - Full variant with app name */}
          <Logo
            variant="full"
            size="sm"
            onClick={handleLogoClick}
            className="cursor-pointer transition-transform hover:scale-105"
          />

          {/* Company Name */}
          {companyName && (
            <>
              <div className="h-6 w-px bg-border/30"></div>
              <div className="text-sm font-bold text-foreground whitespace-nowrap px-1">
                {companyName}
              </div>
            </>
          )}

          {/* Current Route Display - beside company name */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="h-6 w-px bg-border/20"></div>
            <RouteDisplay
              size="md"
              showIcon={false}
              className="text-foreground font-medium"
            />
          </div>
        </div>

        {/* Right Section: Action Buttons */}
        <div className="flex items-center gap-3 ml-auto">
          {/* New Transaction Button */}
          <Button
            onClick={onAddNewTransaction}
            size="sm"
            className="h-9 px-5 rounded-sm text-white dark:text-white hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg font-medium border bg-gradient-to-r from-sidebar-primary to-sidebar-primary/90 hover:from-sidebar-primary/95 hover:to-sidebar-primary/85 dark:from-accent dark:to-accent/90 dark:hover:from-accent/95 dark:hover:to-accent/85"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Nova Transação</span>
            <span className="sm:hidden">Nova</span>
          </Button>

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative group"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-primary/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-muted/80 hover:bg-background rounded-full p-2 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/10">
                    <Bell className="h-5 w-5 text-foreground group-hover:text-primary transition-colors duration-300" />
                  </div>
                  <span className="notification-dot absolute -top-0.5 -right-0.5 h-3 w-3 bg-gradient-to-r from-finance-red to-destructive rounded-full animate-pulse border-2 border-background"></span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-4 py-3 border-b-0">
                <span className="font-semibold text-foreground">Notificações</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toast.success('Todas as notificações foram marcadas como lidas')}
                  className="text-xs hover:bg-muted transition-colors text-foreground"
                >
                  Marcar todas como lidas
                </Button>
              </div>
              <div className="py-8 text-center text-sm text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                <p>Nenhuma notificação no momento</p>
                <p className="text-xs mt-1">Fique atento às atualizações!</p>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-10 px-3 rounded-md text-foreground/80 hover:text-foreground hover:bg-muted transition-all duration-200 flex items-center gap-3"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={companyLogoUrl || undefined} />
                  <AvatarFallback className="bg-gradient-to-br from-sidebar-accent to-sidebar-accent/80 text-sidebar-accent-foreground font-semibold border-2 border-sidebar-accent/60">
                    {companyName?.[0] || <Building2 className="h-4 w-4" />}
                  </AvatarFallback>
                </Avatar>
                {companyName && (
                  <span className="text-sm font-medium text-foreground whitespace-nowrap hidden sm:inline">
                    {companyName}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-72 overflow-hidden border-0 shadow-2xl backdrop-blur-sm bg-card"
              sideOffset={8}
            >
              {/* Header Section with User Info */}
              <div className="relative bg-gradient-to-br from-background via-blue-medium-50/30 to-blue-dark-50/20 px-4 py-5">
                {/* Decorative top gradient - Nova paleta azul */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary"></div>

                <div className="flex items-center gap-4">
                  <div className="relative">
                    {/* Avatar with warm glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/15 rounded-full blur-lg scale-110"></div>
                    <Avatar className="relative h-12 w-12 border-2 border-background/60 shadow-lg">
                      <AvatarImage src={companyLogoUrl || undefined} className="object-cover" />
                      <AvatarFallback className="bg-gradient-to-br from-primary via-accent to-secondary text-white font-bold text-lg border-2 border-background/40 shadow-inner">
                        {companyName?.[0] || <Building2 className="h-6 w-6" />}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  <div className="flex flex-col flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground leading-tight truncate">
                      {companyName || profile?.full_name || 'Usuário'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {user?.email}
                    </p>
                  </div>

                  {/* Bauhaus decorative element */}
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-success to-success shadow-lg shadow-success/30 animate-pulse"></div>
                </div>
              </div>

              {/* Menu Items Section */}
              <div className="bg-card/95">
                <div className="p-1 space-y-0.5">
                  {/* Profile Menu Item */}
                  <DropdownMenuItem
                    onClick={() => window.location.href = '/settings/profile'}
                    className="group relative overflow-hidden cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-gradient-to-r hover:from-blue-dark-50 hover:to-blue-medium-30 transition-all duration-200"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-transparent to-accent/0 group-hover:from-primary/10 group-hover:via-accent/5 group-hover:to-accent/10 transition-all duration-300"></div>
                    <div className="relative flex items-center">
                      <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-dark-100 to-blue-medium-100 group-hover:scale-110 transition-transform duration-200">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Meu Perfil</span>
                        <span className="text-xs text-muted-foreground">Gerenciar informações</span>
                      </div>
                    </div>
                  </DropdownMenuItem>

                  {/* Settings Menu Item */}
                  <DropdownMenuItem
                    onClick={() => window.location.href = '/settings'}
                    className="group relative overflow-hidden cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-gradient-to-r hover:from-blue-medium-50 hover:to-secondary/30 transition-all duration-200"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary/0 via-transparent to-primary/0 group-hover:from-secondary/10 group-hover:via-primary/5 group-hover:to-primary/10 transition-all duration-300"></div>
                    <div className="relative flex items-center">
                      <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-medium-100 to-primary/100 group-hover:scale-110 transition-transform duration-200">
                        <Settings className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Configurações</span>
                        <span className="text-xs text-muted-foreground">Preferências do sistema</span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </div>

                {/* Separator with Bauhaus gradient */}
                <div className="mx-3 my-2 h-px bg-gradient-to-r from-transparent via-muted to-transparent"></div>

                {/* Logout Section */}
                <div className="px-3 pb-2">
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="group relative overflow-hidden cursor-pointer rounded-lg px-3 py-2.5 text-finance-red hover:bg-gradient-to-r hover:from-finance-red/10 hover:to-destructive/10 transition-all duration-200"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-finance-red/0 via-transparent to-destructive/0 group-hover:from-finance-red/10 group-hover:via-destructive/5 group-hover:to-destructive/10 transition-all duration-300"></div>
                    <div className="relative flex items-center">
                      <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-finance-red/20 to-destructive/20 group-hover:scale-110 transition-transform duration-200">
                        <svg className="h-5 w-5 text-finance-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Sair</span>
                        <span className="text-xs text-muted-foreground group-hover:text-finance-red/70">Encerrar sessão</span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </div>

                {/* Footer with Bauhaus decorative element */}
                <div className="relative h-8 overflow-hidden bg-gradient-to-br from-background to-muted/30">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/3 via-accent/3 to-secondary/3"></div>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary opacity-30"></div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
